<template>
    <b-modal :id="id" dialog-class="pdf-chopper-dialog" v-model="visible" size="xl" title="Split Document"
        @show="visibleToggled" @ok="okClicked" scrollable hide-header-close>
        <b-row v-if="loading" class="source">
            <b-col cols="12" class="text-center py-4 text-muted">
                <b-spinner class="align-middle" />
                <span class="ml-2">Loading pages…</span>
            </b-col>
        </b-row>
        <b-row v-else-if="imageSrc?.length" class="source">
            <b-col>
                <b-row align-v="center" class="mb-2 selection-toolbar">
                    <b-col cols="auto">
                        <h6 class="mb-0">Source Pages</h6>
                    </b-col>
                    <b-col cols="auto" class="ml-auto d-flex align-items-center">
                        <span class="text-muted mr-3" :class="{ 'invisible': !selectedImages.length }">{{ selectedImages.length || 0 }} of {{ imageSrc?.length }} selected</span>
                        <b-dropdown text="Move Selected To" size="sm" variant="primary" class="mr-3" :class="{ 'invisible': !selectedImages.length }">
                            <b-dropdown-item v-for="(group, idx) in groups" :key="'move-to-' + idx" @click="moveSelectedToGroup(idx)">
                                Group {{ idx + 1 }}{{ group.destination[0] ? ' - ' + group.destination[0] : '' }}
                            </b-dropdown-item>
                        </b-dropdown>
                        <b-form-checkbox
                            :checked="selectedImages.length === imageSrc?.length && imageSrc?.length > 0"
                            :indeterminate="selectedImages.length > 0 && selectedImages.length < (imageSrc?.length ?? 0)"
                            @change="$event ? selectAllImages() : clearSelection()">
                            Select All
                        </b-form-checkbox>
                    </b-col>
                </b-row>
                <draggable v-model="imageSrc" :draggable="'.item'" group="pages"
                    :class="'row row-cols-8' + draggableClass" :force-fallback="true"
                    @change="onSourceChange" @start="onDragStart" @end="onDragEnd">
                    <b-col v-for="(img, index) in imageSrc" :key="'image_' + (index - 1)"
                        class="item position-relative" :class="{'selected-image': isSelected(img), 'current-preview': isWideScreen && isCurrentPreview(img)}">
                        <b-form-checkbox
                            class="image-checkbox"
                            :checked="isSelected(img)"
                            @change="toggleImageSelection(img, $event)"
                            @click.native.stop="handleCheckboxClick(img, $event)" />
                        <b-img :src="img.url" thumbnail block
                            @click="toggleImageSelection(img, undefined, $event)"
                            @mouseenter="showPreview(img)"
                            @mouseleave="hidePreview()" />
                    </b-col>
                </draggable>
            </b-col>
        </b-row>
        <b-row id="destination-row">
            <b-col cols="12" v-for="(group, index) in groups" :key="'group_' + (index + 1)">
                <b-row>
                    <b-col>
                        <h6 class="mt-3">
                            Group {{ index + 1 }}
                            <template v-if="group.destination[0]">
                                - {{ group.destination[0] }}
                            </template>
                        </h6>
                    </b-col>
                </b-row>
                <b-row align-v="end">
                    <b-col cols="auto">
                        Destination:
                    </b-col>
                    <b-col cols="auto">
                        <b-form-checkbox-group v-model="groups[index].destination" buttons size="sm">
                            <b-form-checkbox value="Patient Document" @change="filterGroups"
                                :button-variant="variant(index, 'Patient Document')">
                                Patient Document
                            </b-form-checkbox>
                            <b-form-checkbox v-if="!isPatientDocumentContext" value="New Script" @change="filterGroups"
                                :button-variant="variant(index, 'New Script')">
                                New Script
                            </b-form-checkbox>
                            <b-form-checkbox value="Discard" @change="filterGroups"
                                :button-variant="variant(index, 'Discard')">
                                Discard
                            </b-form-checkbox>
                            <b-form-checkbox v-if="!isPatientDocumentContext" value="Original Script" @change="filterGroups"
                                :button-variant="variant(index, 'Original Script')"
                                :disabled="isOriginalScriptDisabled(index)"
                                :title="getOriginalScriptTooltip(index)">
                                Original Script
                            </b-form-checkbox>
                            <b-form-checkbox v-else value="Original Document" @change="filterGroups"
                                :button-variant="variant(index, 'Original Document')"
                                :disabled="isOriginalDocumentDisabled(index)"
                                :title="getOriginalDocumentTooltip(index)">
                                Original Document
                            </b-form-checkbox>
                        </b-form-checkbox-group>
                    </b-col>
                </b-row>
                <b-row v-if="groups[index].destination.includes('Patient Document')" class="mt-1" align-v="baseline">
                    <b-col>
                        <b-input v-model="groups[index].fileName" placeholder="File Name"
                            :state="fileNameInputState(groups[index].fileName)" required />
                    </b-col>
                    <b-col v-if="!isPatientDocumentContext">
                        <search-component :id="groups[index].patient && +groups[index].patient.id"
                            v-model="groups[index].patient" :hide-label="true" placeholder="Patient"
                            search-u-r-l="/Patient" create-new-object clearable createNewLabel="Create New Patient"
                            @createNewObject="groups[index].showPatientForm = true" required
                            :state="patientSearchInputState(groups[index].patient)" />
                    </b-col>
                    <b-col cols="12" v-if="availableTags?.length">
                        <label>Tags<span v-if="tagsRequired"> *</span></label>
                        <multiselect v-model="groups[index].tags" :options="availableTags" multiple />
                    </b-col>
                    <b-col cols="12" class="mt-2">
                        <acceptance-config-selector
                            :enabled.sync="groups[index].createAcceptanceOnSave"
                            :configuration.sync="groups[index].acceptanceConfig"
                            :source-type.sync="groups[index].acceptanceSourceType" />
                    </b-col>

                </b-row>
                <b-row :class="(groups[index].showPatientForm ? 'sticky' : '')">
                    <b-col>
                        <!--suppress HtmlWrongAttributeValue draggable doesn't like .item-->
                        <draggable v-model="groups[index].images" draggable=".item" group="pages"
                            class="w-100 h-100 text-center mt-1 row row-cols-8" style="border: 1px dashed; min-height: 100px;">
                            <template v-if="!groups[index].images.length" #header>
                                <b-col cols="12" class="drop-hint">
                                    Drop pages here.
                                </b-col>
                            </template>
                            <b-col v-for="(img, idx) in groups[index].images"
                                :key="'group_' + index + '_image_' + (idx - 1)" class="item"
                                :class="{ 'current-preview': isWideScreen && isCurrentPreview(img) }"
                                @click="selectedImage = img.url" >
                                <b-img :src="img.url" thumbnail block
                                    @mouseenter="showPreview(img)"
                                    @mouseleave="hidePreview()" />
                            </b-col>
                        </draggable>
                    </b-col>
                </b-row>
                <b-row v-if="groups[index].showPatientForm">
                    <b-col cols="5">
                        <b-img :src="srcForGroup(groups[index])" class="sticky-2"/>
                    </b-col>
                    <b-col v-if="groups[index].showPatientForm">
                        <PatientFormComponent :button-titles="patientButtonTitles" create-new-patient hide-insurance
                            @close="closePatientForm" @created="onPatientCreated" />
                    </b-col>
                </b-row>
            </b-col>

            <b-col v-if="groups.length < (totalPages ?? 0)" cols="12">
                <b-button variant="success" @click="addGroup">
                    Add Group
                </b-button>
            </b-col>
        </b-row>
        <div v-if="displayedPreview" class="preview-panel" :class="{ 'always-visible': isWideScreen }">
            <img :src="previewUrl" class="preview-image" />
            <div class="preview-label">
                Page {{ displayedPreview.pageNumber + 1 }}
                <span v-if="displayedPreview.hiResLoading" class="hi-res-loading">(loading HD...)</span>
            </div>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Vue, Component, Prop, VModel } from 'vue-property-decorator';
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import draggable from 'vuedraggable';
import { BvEvent } from "bootstrap-vue";
import SearchComponent from "@/components/SearchComponent.vue";
import { Patient } from "@/models/Patient";
import { QueueItem } from "@/models/QueueItem";
import { AcceptanceConfiguration } from "@/models/AcceptanceConfiguration";
import { AcceptanceSource } from "@/models/AcceptanceSource";
import AcceptanceConfigSelector from "@/components/Acceptance/AcceptanceConfigSelector.vue";
import PatientFormComponent from "@/components/Patient/PatientFormComponent.vue";
import Multiselect from "vue-multiselect";

type PageImage = {
    pageNumber: number,
    url: string,
    hiResUrl?: string,
    hiResLoading?: boolean,
}
type Group = {
    destination: string[],
    images: PageImage[],
    fileName: string | null,
    tags: string[],
    patient: Patient,
    showPatientForm: boolean,
    createAcceptanceOnSave: boolean,
    acceptanceConfig: AcceptanceConfiguration | null,
    acceptanceSourceType: AcceptanceSource | null,
}
@Component({
    name: "PdfChopperModal",
    components: {
        AcceptanceConfigSelector,
        Multiselect,
        PatientFormComponent,
        SearchComponent,
        draggable,
    },
})
export default class PdfChopperModal extends Vue {
    @VModel() visible!: boolean;
    @Prop({ type: String, default: "pdf-chopper" }) id!: string;
    @Prop() imageId!: number;
    @Prop({ type: Boolean, default: false }) hasSavedPrescription!: boolean;
    // Which surface launched the modal. "patientDocument" swaps the prescription-specific
    // destinations (New Script / Original Script) for the patient-documents equivalents.
    @Prop({ type: String, default: "prescription" }) context!: string;
    // Source patient document's patient, pre-filled onto Patient Document groups so split pages
    // stay with the same patient (patient-document context only).
    @Prop() patient?: Patient;
    // Source patient document id, repointed by the "Original Document" destination.
    @Prop() sourcePatientDocumentId?: number;

    get isPatientDocumentContext(): boolean {
        return this.context === "patientDocument";
    }
    protected imageSrc?: PageImage[] = [];
    protected pages: number[] = [];
    protected groups: Group[] = [
        this.createEmptyGroup(),
        this.createEmptyGroup(),
    ];
    protected availableTags: string[] = [];
    private requirePatientDocumentTags = false;

    protected patientButtonTitles = {
        new: "",
        clear: "",
        save: "Save Patient",
        cancel: "Close Patient Form",
    };

    protected selectedImage: string = "";
    protected selectedImages: PageImage[] = [];
    protected isDraggingMultiple = false;
    private multiDragRaf: number | null = null;
    protected lastSelectedIndex: number | null = null;
    protected hoveredImage: PageImage | null = null;
    protected previewImage: PageImage | null = null;
    private previewTimeout: number | null = null;
    protected isWideScreen = false;
    private resizeHandler: (() => void) | null = null;

    get displayedPreview(): PageImage | null {
        // On wide screens, always show something: current preview, or first page
        if (this.isWideScreen) {
            const preview = this.previewImage || this.imageSrc?.[0] || null;
            // Trigger high-res load when preview changes on wide screens
            if (preview && !preview.hiResUrl && !preview.hiResLoading) {
                this.loadHiResImage(preview);
            }
            return preview;
        }
        // On narrow screens, only show when actively previewing
        return this.previewImage;
    }

    get previewUrl(): string {
        const preview = this.displayedPreview;
        if (!preview) return '';
        // Use high-res URL on wide screens when available
        if (this.isWideScreen && preview.hiResUrl) {
            return preview.hiResUrl;
        }
        return preview.url;
    }

    mounted() {
        this.checkWideScreen();
        this.resizeHandler = () => this.checkWideScreen();
        window.addEventListener('resize', this.resizeHandler);
    }

    beforeDestroy() {
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
        // Clean up object URLs to prevent memory leaks
        this.cleanupHiResUrls();
    }

    cleanupHiResUrls() {
        const allImages = [
            ...(this.imageSrc || []),
            ...this.groups.flatMap(g => g.images)
        ];
        for (const img of allImages) {
            if (img.hiResUrl) {
                URL.revokeObjectURL(img.hiResUrl);
            }
        }
    }

    checkWideScreen() {
        this.isWideScreen = window.innerWidth >= 1600;
    }

    async loadHiResImage(img: PageImage) {
        if (img.hiResUrl || img.hiResLoading) return;

        // Mark as loading
        this.$set(img, 'hiResLoading', true);

        try {
            const response = await axios.get(`/api/image/${this.imageId}/page/${img.pageNumber}?height=1500`, {
                responseType: 'blob'
            });
            const blob = response.data;
            const hiResUrl = URL.createObjectURL(blob);
            this.$set(img, 'hiResUrl', hiResUrl);
        } catch (err) {
            console.error('Failed to load high-res image:', err);
        } finally {
            this.$set(img, 'hiResLoading', false);
        }
    }

    showPreview(img: PageImage) {
        this.hoveredImage = img;
        if (this.previewTimeout) {
            clearTimeout(this.previewTimeout);
        }
        // On wide screens, show preview immediately; on narrow screens, delay
        const delay = this.isWideScreen ? 0 : 750;
        this.previewTimeout = window.setTimeout(() => {
            this.previewImage = this.hoveredImage;
        }, delay);
    }

    hidePreview() {
        this.hoveredImage = null;
        if (this.previewTimeout) {
            clearTimeout(this.previewTimeout);
            this.previewTimeout = null;
        }
        // On wide screens, keep showing the last previewed image
        if (!this.isWideScreen) {
            this.previewImage = null;
        }
    }

    closePatientForm() {
        this.groups.forEach(g => g.showPatientForm = false);
    }

    onPatientCreated(newPatient: Patient) {
        this.groups.forEach(g => {
            if (g.showPatientForm) {
                g.patient = newPatient;
                g.showPatientForm = false;
            }
        });
    }

    protected loading: boolean = false;
    private shouldValidate = false;
    private isProcessing: boolean = false;

    created() {
        console.log("%c Created PdfChopperModal", "color: green;");
        this.getTags();
        this.getConfiguration();
    }

    createEmptyGroup(): Group {
        return {
            destination: [],
            images: [],
            fileName: null,
            tags: [],
            // In the patient-documents context every Patient Document group is destined for the
            // source document's patient, so pre-fill it (the patient search is hidden there).
            patient: this.isPatientDocumentContext && this.patient ? this.patient : new Patient(),
            showPatientForm: false,
            createAcceptanceOnSave: false,
            acceptanceConfig: null,
            acceptanceSourceType: null,
        };
    }

    filterGroups() {
        // Collapse to the single most-recently-picked destination, mutating in place so any
        // fields not touched here survive untouched.
        this.groups.forEach(g => {
            g.destination = [g.destination[g.destination.length - 1]];
            // Acceptance records only apply to Patient Document groups; drop any opted-in
            // selection when the group is no longer destined for one.
            if (!g.destination.includes("Patient Document")) {
                g.createAcceptanceOnSave = false;
                g.acceptanceConfig = null;
                g.acceptanceSourceType = null;
            }
        });
    }

    addGroup() {
        this.groups.push(this.createEmptyGroup());
    }

    handleCheckboxClick(img: PageImage, event: MouseEvent): void {
        // Handle shift+click for range selection on checkbox
        if (event.shiftKey && this.lastSelectedIndex !== null && this.imageSrc) {
            const currentIndex = this.imageSrc.findIndex(i => i.pageNumber === img.pageNumber);
            if (currentIndex !== -1) {
                event.preventDefault();
                const start = Math.min(this.lastSelectedIndex, currentIndex);
                const end = Math.max(this.lastSelectedIndex, currentIndex);
                const rangeImages = this.imageSrc.slice(start, end + 1);

                // Add all images in range to selection (without duplicates)
                for (const rangeImg of rangeImages) {
                    if (!this.selectedImages.some(i => i.pageNumber === rangeImg.pageNumber)) {
                        this.selectedImages.push(rangeImg);
                    }
                }
            }
        }
    }

    toggleImageSelection(img: PageImage, e?: boolean, event?: MouseEvent): void {
        const currentIndex = this.imageSrc?.findIndex(i => i.pageNumber === img.pageNumber) ?? -1;

        // Handle shift+click for range selection
        if (event?.shiftKey && this.lastSelectedIndex !== null && currentIndex !== -1 && this.imageSrc) {
            const start = Math.min(this.lastSelectedIndex, currentIndex);
            const end = Math.max(this.lastSelectedIndex, currentIndex);
            const rangeImages = this.imageSrc.slice(start, end + 1);

            // Add all images in range to selection (without duplicates)
            for (const rangeImg of rangeImages) {
                if (!this.selectedImages.some(i => i.pageNumber === rangeImg.pageNumber)) {
                    this.selectedImages.push(rangeImg);
                }
            }
            return;
        }

        const hasItem = this.selectedImages.some(i => i.pageNumber === img.pageNumber);
        this.selectedImages = this.selectedImages.filter(i => i.pageNumber !== img.pageNumber);
        if (e || (e === undefined && !hasItem)) {
            this.selectedImages.push(img);
            this.lastSelectedIndex = currentIndex;
        } else {
            // If deselecting, don't update lastSelectedIndex
        }
    }

    isSelected(img: PageImage): boolean {
        return this.selectedImages.some(i => i.pageNumber === img.pageNumber);
    }

    isCurrentPreview(img: PageImage): boolean {
        return this.displayedPreview?.pageNumber === img.pageNumber;
    }

    selectAllImages(): void {
        this.selectedImages = [...(this.imageSrc ?? [])];
    }

    clearSelection(): void {
        this.selectedImages = [];
        this.lastSelectedIndex = null;
    }

    moveSelectedToGroup(groupIndex: number): void {
        if (!this.selectedImages.length) return;
        // Add selected images to target group
        this.groups[groupIndex].images.push(...this.selectedImages);
        // Remove from source
        this.imageSrc = this.imageSrc?.filter(
            img => !this.selectedImages.some(s => s.pageNumber === img.pageNumber)
        );
        this.clearSelection();
    }

    onSourceChange(evt: any): void {
        // If an item was removed and it was selected, move all selected
        if (evt.removed && this.isSelected(evt.removed.element) && this.selectedImages.length > 1) {
            // Find which group received the item
            for (let i = 0; i < this.groups.length; i++) {
                const idx = this.groups[i].images.findIndex(
                    img => img.pageNumber === evt.removed.element.pageNumber
                );
                if (idx >= 0) {
                    // Move remaining selected images to this group (excluding the one already moved)
                    const remainingSelected = this.selectedImages.filter(
                        s => s.pageNumber !== evt.removed.element.pageNumber
                    );
                    this.groups[i].images.push(...remainingSelected);
                    this.imageSrc = this.imageSrc?.filter(
                        img => !remainingSelected.some(s => s.pageNumber === img.pageNumber)
                    );
                    this.clearSelection();
                    break;
                }
            }
        }
    }

    onDragStart(evt: any): void {
        // Only decorate the ghost for a genuine multi-page drag: more than one
        // page selected AND the page being dragged is part of that selection
        // (matches the move logic in onSourceChange). Otherwise leave the
        // default single-page ghost alone.
        const dragged = this.imageSrc?.[evt?.oldIndex];
        if (!dragged || !this.isSelected(dragged) || this.selectedImages.length <= 1) {
            return;
        }

        this.isDraggingMultiple = true;

        // With force-fallback, SortableJS creates the .sortable-fallback clone
        // lazily on the first pointer move, so it isn't in the DOM yet. Poll a
        // few frames until it appears, then add the stacked-card decoration once.
        const count = this.selectedImages.length;
        let frames = 0;
        const tryDecorate = () => {
            this.multiDragRaf = null;
            const ghost = document.querySelector<HTMLElement>(".sortable-fallback");
            if (ghost) {
                this.decorateMultiDragGhost(ghost, count);
                return;
            }
            if (++frames < 30) {
                this.multiDragRaf = requestAnimationFrame(tryDecorate);
            }
        };
        this.multiDragRaf = requestAnimationFrame(tryDecorate);
    }

    onDragEnd(): void {
        this.isDraggingMultiple = false;
        if (this.multiDragRaf !== null) {
            cancelAnimationFrame(this.multiDragRaf);
            this.multiDragRaf = null;
        }
        // The decoration normally leaves with the ghost Sortable tears down;
        // remove any stragglers defensively.
        document.querySelectorAll(".multi-drag-decoration").forEach(n => n.remove());
    }

    private decorateMultiDragGhost(ghost: HTMLElement, count: number): void {
        if (ghost.querySelector(".multi-drag-decoration")) return; // already decorated

        const badge = document.createElement("div");
        badge.className = "multi-drag-decoration multi-drag-count";
        badge.textContent = String(count);
        ghost.appendChild(badge);
    }

    visibleToggled() {
        // Revoke the prior session's hi-res blob URLs before the resets below
        // discard the old pages/groups, otherwise they leak until beforeDestroy.
        this.cleanupHiResUrls();

        // Clear any preview lingering from the previous session, otherwise the
        // last-hovered page reappears on reopen pointing at a hi-res blob URL
        // that cleanupHiResUrls just revoked (broken image).
        if (this.previewTimeout) {
            clearTimeout(this.previewTimeout);
            this.previewTimeout = null;
        }
        this.previewImage = null;
        this.hoveredImage = null;
        this.selectedImage = "";

        this.groups = [];
        this.addGroup();
        this.addGroup();
        this.selectedImages = [];
        this.lastSelectedIndex = null;

        this.fetchImageSource();
    }

    variant(index: number, value: string): string {
        if (this.groups[index] == undefined || this.groups[index].destination == undefined) {
            console.log("secondary: undefined!!", index, value, this.groups[index]);
            return "secondary";
        }
        if (this.groups[index].destination.includes(value)) {
            return "success";
        }

        if (this.shouldValidate && this.groups[index].images.length) {
            if ((this.groups[index].destination?.length ?? 0) === 0) {
                return "danger";
            }
            if (this.groups[index].destination[0] === undefined) {
                return "danger";
            }
        }
        return "secondary";
    }

    isOriginalScriptDisabled(index: number): boolean {
        if (!this.hasSavedPrescription) return true;
        return this.groups.some((g, i) => i !== index && g.destination.includes("Original Script"));
    }

    getOriginalScriptTooltip(index: number): string {
        if (!this.hasSavedPrescription) return "Save the prescription first to keep pages with it";
        if (this.groups.some((g, i) => i !== index && g.destination.includes("Original Script"))) {
            return "Only one group can keep pages with the original";
        }
        return "";
    }

    isOriginalDocumentDisabled(index: number): boolean {
        // Only one group can be written back to the source document.
        return this.groups.some((g, i) => i !== index && g.destination.includes("Original Document"));
    }

    getOriginalDocumentTooltip(index: number): string {
        if (this.isOriginalDocumentDisabled(index)) {
            return "Only one group can be kept as the original document";
        }
        return "";
    }

    srcForGroup(group: any) {
        if (this.selectedImage?.length > 0) return this.selectedImage;
        return group.images[0]?.url;
    }

    get draggableClass(): string {
        if (this.shouldValidate && !this.imageSrc?.length) return " is-invalid";
        return "";
    }

    patientSearchInputState(patient: Patient): boolean | null {
        if (!this.shouldValidate) return null;
        return !!patient?.id;
    }

    fileNameInputState(fileName: string | null): boolean | null {
        if (!this.shouldValidate) return null;
        return !!fileName?.length;
    }

    validate(): boolean {
        this.shouldValidate = true;
        if (this.imageSrc?.length ?? 0 > 0) {
            this.$notification(NotificationOptions.error("You must move all pages to a group."));
            return false;
        }

        const hasIncompletePatientDocuments = this.groups.some(g => g.destination.includes("Patient Document") && g.images.length > 0 &&
            (g.fileName == null || g.fileName.trim() === "" || g.patient == null || g.patient.id == null || g.patient.id === 0));
        if (hasIncompletePatientDocuments) {
            this.$notification(NotificationOptions.error("Patient Document sections must have a patient and file name specified."));
            return false;
        }

        const hasIncompleteDestinations = this.groups.some(g => g.images.length > 0 && (g.destination.length === 0 || g.destination[0] === undefined));
        if (hasIncompleteDestinations) {
            this.$notification(NotificationOptions.error("You must specify a destination for each group with pages in it."));
            return false;
        }

        // Check if tags are required and missing for Patient Document groups
        if (this.tagsRequired) {
            const hasMissingTags = this.groups.some(g => g.destination.includes("Patient Document") && g.images.length > 0 &&
                (!g.tags || g.tags.length === 0));
            if (hasMissingTags) {
                this.$notification(NotificationOptions.error("Patient Document sections must have at least one tag selected."));
                return false;
            }
        }

        // A Patient Document group opting into an acceptance record must have both a
        // configuration and a source type selected.
        const hasIncompleteAcceptance = this.groups.some(g => g.destination.includes("Patient Document") && g.images.length > 0 &&
            g.createAcceptanceOnSave && (!g.acceptanceConfig?.id || g.acceptanceSourceType === null));
        if (hasIncompleteAcceptance) {
            this.$notification(NotificationOptions.error("Acceptance records require both a configuration and a source type."));
            return false;
        }

        return true;
    }

    async okClicked(event: BvEvent) {
        event.preventDefault();

        if (this.isProcessing) return;  // Guard against multiple clicks

        if (!this.validate()) return;

        this.isProcessing = true;
        try {
            type SplitResponse = {
                newQueueItems: QueueItem[];
                originalScriptUsed: boolean;
                newImageId: number | null;
                acceptanceRecordFailed: boolean;
            };
            // Only forward acceptance fields for Patient Document groups that opted in,
            // and drop the heavy config object the backend doesn't use (undefined is
            // omitted from the serialized request).
            const payload = this.groups.map(g => ({
                ...g,
                acceptanceConfig: undefined,
                acceptanceConfigurationId: g.createAcceptanceOnSave ? g.acceptanceConfig?.id ?? null : null,
                acceptanceSourceType: g.createAcceptanceOnSave ? g.acceptanceSourceType : null,
                // Source document the "Original Document" destination repoints (patient-document context).
                patientDocumentId: this.sourcePatientDocumentId ?? null,
            }));
            const response = await axios.post<SplitResponse>(`/api/image/${this.imageId}/split`, payload);
            if (response.data.acceptanceRecordFailed) {
                this.$notification(NotificationOptions.notificationOptionsPreset(
                    "Document(s) saved but failed to create acceptance record",
                    "warning"
                ));
            }
            this.$emit("splitComplete", response.data);
            if ((event.vueTarget as any).hide) {
                (event.vueTarget as any).hide();
            }
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isProcessing = false;
        }
    }

    async getTags() {
        try {
            const response = await axios.get("/api/Configuration/PatientDocumentTags");
            this.availableTags = response.data;
        } catch (err) {
            console.error(err);
        }
    }

    async getConfiguration() {
        try {
            const response = await axios.get("/api/Configuration/RequirePatientDocumentTags");
            this.requirePatientDocumentTags = response.data;
        } catch (err) {
            console.error(err);
        }
    }

    get tagsRequired() {
        return this.requirePatientDocumentTags && this.availableTags.length > 0;
    }

    protected totalPages = 0;
    private fetchSequence = 0;

    async fetchImageSource() {
        // Guard against overlapping fetches (open -> cancel -> reopen): only the
        // latest request populates state, built into a local array so we never
        // append across the await and duplicate pages (SD-4159).
        const requestId = ++this.fetchSequence;
        try {
            this.shouldValidate = false;
            this.loading = true;
            this.imageSrc = [];
            const imageUrl = `/api/image/${this.imageId}/pages`;

            const imageResp = await axios.get<[]>(imageUrl);
            if (requestId !== this.fetchSequence) return; // stale response, a newer open superseded it

            const pages: PageImage[] = [];
            for (let i = 0; i < imageResp.data.length; i++) {
                const blob = imageResp.data[i];
                pages.push({ pageNumber: i, url: `data:image/webp;base64,${blob}` });
            }
            this.imageSrc = pages;
            this.totalPages = pages.length;
        } catch (err) {
            if (requestId === this.fetchSequence) {
                this.$notification(NotificationOptions.error(err));
            }
        } finally {
            if (requestId === this.fetchSequence) {
                this.loading = false;
            }
        }
    }

}
</script>

<style scoped>
.item {
    margin-bottom: 1rem;
}

.selection-toolbar {
    min-height: 38px;
}

.selection-toolbar ::v-deep .dropdown-menu {
    z-index: 1100;
}

.row-cols-8 > * {
    flex: 0 0 12.5%;
    max-width: 12.5%;
}

.row-cols-8 > .drop-hint {
    flex: 0 0 100%;
    max-width: 100%;
}

.preview-panel {
    position: fixed;
    bottom: 16px;
    right: 16px;
    z-index: 1100;
    background: white;
    border: 2px solid #dee2e6;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    padding: 8px;
    max-width: 700px;
    max-height: 85vh;
    pointer-events: none;
}

.preview-image {
    max-width: 100%;
    max-height: calc(85vh - 40px);
    display: block;
}

/* Wide screen: center preview in the reserved 720px space */
@media (min-width: 1600px) {
    .preview-panel {
        top: 50%;
        bottom: auto;
        right: 16px;
        transform: translateY(-50%);
        width: 688px;
        max-width: 688px;
        max-height: 90vh;
    }

    .preview-image {
        max-height: calc(90vh - 60px);
        width: 100%;
        object-fit: contain;
    }
}

.preview-label {
    text-align: center;
    padding-top: 8px;
    font-weight: 500;
    color: #495057;
}

.hi-res-loading {
    font-size: 0.85em;
    color: #6c757d;
    font-weight: normal;
}

/*noinspection CssUnusedSymbol*/
.is-invalid {
    border: 1px solid #dc3545;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right calc(0.375em + 0.1875rem) center;
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

.source {
    position: sticky;
    top: -24px;
    z-index: 300;
    padding: 10px 0;
    background: white;
}
.sticky {
    position: sticky;
    top: -24px;
    z-index: 300;
    padding: 10px 0;
    background: white;
}
.sticky-2 {
    position: sticky;
    top: 120px;
    z-index: 200;
    padding: 10px 0;
    background: white;
    width: 100%;
}

.selected-image ::v-deep .img-thumbnail {
    outline: 3px solid #007bff;
    outline-offset: -3px;
    background-color: rgba(0, 123, 255, 0.1);
}

.current-preview ::v-deep .img-thumbnail {
    box-shadow: 0 0 0 3px #28a745;
}

.current-preview::after {
    content: '';
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid #28a745;
}

.image-checkbox {
    position: absolute;
    top: 8px;
    left: 12px;
    z-index: 1001;
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: auto;
}

.item:hover .image-checkbox,
.selected-image .image-checkbox {
    opacity: 1;
}

.image-checkbox ::v-deep .custom-control-label::before {
    background-color: rgba(255, 255, 255, 0.9);
    border: 2px solid #6c757d;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.image-checkbox ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #007bff;
    border-color: #007bff;
}

/* The thumbnails carry Bootstrap's `.position-relative` utility (so the absolute-
   positioned checkbox can anchor to them). Sortable's force-fallback ghost is a
   clone of the thumbnail and inherits that class — Bootstrap declares
   `.position-relative { position: relative !important }`, which beats the inline
   `position: fixed` Sortable applies to the ghost. The result is the ghost
   renders at its natural-flow offset instead of viewport coordinates, drifting
   far from the cursor (and off-screen on wide layouts). Restore fixed positioning
   on the ghost specifically. */
.sortable-fallback {
    position: fixed !important;
}

</style>

<!-- Unscoped styles for modal positioning (Bootstrap Vue modals are portaled to body) -->
<style>
/* Wide screen layout: position modal to the left, reserving space for the preview panel.
   Keyed to dialog-class rather than the modal id so every call site is shifted regardless
   of the id prop it passes (the preview panel is fixed to the viewport for all instances). */
@media (min-width: 1600px) {
    .pdf-chopper-dialog {
        max-width: calc(100vw - 740px) !important;
        margin-left: 20px !important;
        margin-right: 720px !important;
    }
}

/* Multi-page drag ghost count badge. Injected via JS into the force-fallback
   ghost (.sortable-fallback), so it has no scoped data-v attribute and must be
   styled globally. */
.multi-drag-count {
    position: absolute;
    bottom: -6px;
    right: -6px;
    z-index: 2;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 11px;
    background: #007bff;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    line-height: 22px;
    text-align: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}
</style>
